import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { usePatient, usePatientEvents } from '../../hooks/usePatient';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getNotes, createNote } from '../../api/notes';
import { getPatientQRToken } from '../../api/patients';
import { ColorCodedEvent } from '../../components/ColorCodedEvent';
import { LoadingScreen } from '../../components/LoadingScreen';
import { getAppointments } from '../../api/appointments';
import type { PatientEvent } from '../../api/patients';
import type { Note } from '../../api/notes';
import type { Appointment } from '../../api/appointments';

const getCurrentTimeString = (): string => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const toMins = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const getStatus = (eventTime: string, now: string): 'past' | 'current' | 'upcoming' => {
  const eventMins = toMins(eventTime);
  const nowMins = toMins(now);
  if (nowMins >= eventMins + 45) return 'past';
  if (nowMins >= eventMins) return 'current';
  return 'upcoming';
};

export const PatientDisplay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const patientId = Number(id);
  const queryClient = useQueryClient();
  const [currentTime, setCurrentTime] = useState(getCurrentTimeString());
  const [composeText, setComposeText] = useState('');

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(getCurrentTimeString()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const { data: patient, isLoading: patientLoading } = usePatient(patientId, { enabled: true });
  const { data: events, isLoading: eventsLoading } = usePatientEvents(patientId, { enabled: true });
  const { data: allNotes, isLoading: notesLoading } = useQuery({
    queryKey: ['notes', patientId, 'all'],
    queryFn: () => getNotes(patientId),
    refetchInterval: 60 * 1000,
    enabled: true,
  });
  const { data: qrData } = useQuery({
    queryKey: ['qr-token', patientId],
    queryFn: () => getPatientQRToken(patientId),
    staleTime: 1000 * 60 * 60,
    enabled: true,
  });
  const { data: appointments } = useQuery({
    queryKey: ['appointments', patientId],
    queryFn: () => getAppointments(patientId),
    refetchInterval: 60 * 1000,
    enabled: true,
  });

  const handleSend = async () => {
    if (!composeText.trim()) return;
    try {
      await createNote(patientId, composeText.trim(), 'patient', 'patient_message');
      setComposeText('');
      queryClient.invalidateQueries({ queryKey: ['notes', patientId] });
    } catch { /* silently fail */ }
  };

  if (patientLoading || eventsLoading || notesLoading) {
    return <LoadingScreen message="Loading your day..." />;
  }

  const qrPayload = qrData
    ? btoa(JSON.stringify({ patient_id: patientId, token: qrData.token }))
    : null;

  const todayEvents: PatientEvent[] = events ?? [];
  const messageNotes: Note[] = (allNotes ?? []).filter((n) => n.note_type === 'family_message' || n.note_type === 'patient_message');
  const upcomingAppointments: Appointment[] = appointments ?? [];

  const colBase: React.CSSProperties = {
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif' }}>

      {/* Top bar */}
      <div style={{ height: 56, flexShrink: 0, backgroundColor: '#2c3e50', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px' }}>
        <span style={{ fontSize: 18, color: '#fff' }}>Care Diary</span>
        <span style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>
          {patient ? `${patient.first_name} ${patient.last_name}` : ''}
        </span>
        <span style={{ fontSize: 18, color: '#fff' }}>{currentTime}</span>
      </div>

      {/* Columns */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Col 1 — Today 50% */}
        <div style={{ ...colBase, width: '50%', backgroundColor: '#f0f4ff' }}>
          <div style={{ flexShrink: 0, marginBottom: 12 }}>
            <div style={{ fontSize: 38, fontWeight: 700, color: '#222', lineHeight: 1.2, marginBottom: 2 }}>
              Hello, {patient?.first_name}!
            </div>
            <div style={{ fontSize: 17, color: '#666', marginBottom: 10 }}>
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#444' }}>Today's Schedule</div>
          </div>

          <div style={{ flex: 1, overflow: 'hidden' }}>
            {todayEvents.length === 0 ? (
              <div style={{ fontSize: 20, color: '#888', marginTop: 12 }}>No events scheduled.</div>
            ) : (
              todayEvents.map((event) => (
                <ColorCodedEvent
                  key={event.id}
                  title={event.title}
                  time={event.time}
                  color={event.color}
                  type={event.type}
                  status={getStatus(event.time, currentTime)}
                />
              ))
            )}
          </div>
        </div>

        {/* Col 2 — Tomorrow's events 30% */}
        <div style={{ ...colBase, width: '30%', backgroundColor: '#f5f5f5' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#444', marginBottom: 14, flexShrink: 0 }}>
            Coming Up
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {upcomingAppointments.length === 0 ? (
              <div style={{ fontSize: 16, color: '#aaa', marginTop: 8 }}>No upcoming events</div>
            ) : (
              upcomingAppointments.map((apt) => (
                <div
                  key={apt.id}
                  style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '10px 14px', marginBottom: 10, fontSize: 16, color: '#444', lineHeight: 1.4 }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>{apt.title}</div>
                  <div style={{ fontSize: 13, color: '#888' }}>
                    {new Date(apt.scheduled_at).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                    {' at '}
                    {new Date(apt.scheduled_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  {apt.location && <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>{apt.location}</div>}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Col 3 — Messages & Sign In 20% */}
        <div style={{ ...colBase, width: '20%', backgroundColor: '#fafafa' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#444', marginBottom: 14, flexShrink: 0 }}>
            Messages
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {messageNotes.length === 0 ? (
              <div style={{ fontSize: 15, color: '#aaa' }}>No messages</div>
            ) : (
              messageNotes.map((note) => (
                <div
                  key={note.id}
                  style={{ backgroundColor: '#fffde7', border: '1px solid rgba(249,168,37,0.3)', borderRadius: 10, padding: '10px 12px', marginBottom: 8, fontSize: 15, color: '#5d4037', lineHeight: 1.4, overflow: 'hidden' }}
                >
                  <div style={{ fontSize: 12, color: '#8d6e63', marginBottom: 2 }}>
                    {note.author_name ?? (note.note_type === 'family_message' ? 'Family' : note.note_type === 'patient_message' ? 'You' : 'Carer')}
                  </div>
                  {note.content}
                </div>
              ))
            )}
          </div>

          {/* Compose */}
          <div style={{ flexShrink: 0, padding: '10px 0', borderTop: '1px solid #eee' }}>
            <textarea
              value={composeText}
              onChange={(e) => setComposeText(e.target.value)}
              placeholder="Write a message..."
              style={{ width: '100%', height: 60, resize: 'none', border: '1px solid #ddd', borderRadius: 8, padding: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
            <button
              onClick={handleSend}
              disabled={!composeText.trim()}
              style={{ width: '100%', marginTop: 6, padding: '8px 0', border: 'none', borderRadius: 8, backgroundColor: composeText.trim() ? '#1976d2' : '#ccc', color: '#fff', fontWeight: 700, fontSize: 14, cursor: composeText.trim() ? 'pointer' : 'default' }}
            >
              Send Message
            </button>
          </div>

          {/* QR Sign-In pinned to bottom */}
          <div style={{ flexShrink: 0, paddingTop: 12, borderTop: '1px solid #e8e8e8', textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#555', marginBottom: 8 }}>Carer Sign-In</div>
            {qrPayload ? (
              <QRCodeSVG value={qrPayload} size={100} />
            ) : (
              <div style={{ width: 100, height: 100, backgroundColor: '#eee', borderRadius: 8, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#999' }}>
                QR unavailable
              </div>
            )}
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 6 }}>Scan, then enter PIN</div>
          </div>
        </div>

      </div>
    </div>
  );
};